import { useState, type JSX } from 'react';
import type { KitStockItemDto } from '@ctms/contracts';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
  MapPin,
  Package,
  Search,
} from 'lucide-react';

export interface KitInventoryTableProps {
  stockItems: KitStockItemDto[];
  isLoading?: boolean;
}

export function KitInventoryTable({
  stockItems,
  isLoading = false,
}: KitInventoryTableProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [selectedLabName, setSelectedLabName] = useState<string>('all');
  const [expandedKitIds, setExpandedKitIds] = useState<Set<number>>(new Set());

  // Distinct laboratory names for filtering
  const labNames = Array.from(new Set(stockItems.map((item) => item.laboratoryName)));

  // Filter items
  const filteredItems = stockItems.filter((item) => {
    const matchesSearch =
      search.trim() === '' ||
      item.kitName.toLowerCase().includes(search.toLowerCase()) ||
      item.laboratoryName.toLowerCase().includes(search.toLowerCase()) ||
      (item.localisation && item.localisation.toLowerCase().includes(search.toLowerCase()));

    const matchesLab = selectedLabName === 'all' || item.laboratoryName === selectedLabName;

    return matchesSearch && matchesLab;
  });

  function toggleExpand(kitId: number): void {
    setExpandedKitIds((prev) => {
      const next = new Set(prev);
      if (next.has(kitId)) {
        next.delete(kitId);
      } else {
        next.add(kitId);
      }
      return next;
    });
  }

  function expandAll(): void {
    setExpandedKitIds(new Set(stockItems.map((i) => i.labKitId)));
  }

  function collapseAll(): void {
    setExpandedKitIds(new Set());
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-slate-800 bg-slate-900/50 p-8 text-slate-400">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
          <span className="text-sm">Ładowanie stanu magazynowego...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search & Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
        <div className="flex flex-1 items-center gap-3 min-w-[280px]">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              id="inventory-search-input"
              type="text"
              placeholder="Szukaj po nazwie zestawu, laboratorium lub lokalizacji..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-slate-700 bg-slate-950 pl-9 pr-3 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-hidden"
            />
          </div>

          {labNames.length > 1 && (
            <select
              id="inventory-lab-filter"
              value={selectedLabName}
              onChange={(e) => setSelectedLabName(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 focus:border-cyan-500 focus:outline-hidden"
            >
              <option value="all">Wszystkie laboratoria ({labNames.length})</option>
              {labNames.map((lab) => (
                <option key={lab} value={lab}>
                  {lab}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={expandAll}
            className="rounded-sm px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            Rozwiń wszystkie partie
          </button>
          <span className="text-slate-600">|</span>
          <button
            type="button"
            onClick={collapseAll}
            className="rounded-sm px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            Zwiń
          </button>
        </div>
      </div>

      {/* Stock Table */}
      {filteredItems.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-900/30 p-8 text-center text-slate-400">
          <Package className="h-10 w-10 text-slate-600 mb-2" />
          <p className="text-sm font-medium text-slate-300">Brak zestawów w magazynie</p>
          <p className="text-xs text-slate-500 max-w-md mt-1">
            Nie zarejestrowano jeszcze dostaw zestawów dla tego badania lub żaden zestaw nie pasuje
            do kryteriów wyszukiwania.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-md">
          <table className="w-full text-left text-sm text-slate-200" id="kit-inventory-table">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              <tr>
                <th scope="col" className="w-10 px-4 py-3"></th>
                <th scope="col" className="px-4 py-3">Zestaw laboratoryjny</th>
                <th scope="col" className="px-4 py-3">Laboratorium</th>
                <th scope="col" className="px-4 py-3">Lokalizacja</th>
                <th scope="col" className="px-4 py-3">Typ ważności</th>
                <th scope="col" className="px-4 py-3 text-right">Dostępny stan</th>
                <th scope="col" className="px-4 py-3 text-right">Liczba partii</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredItems.map((item) => {
                const isExpanded = expandedKitIds.has(item.labKitId);
                const hasExpiredBatches = item.batches.some((b) => b.isExpired);
                const hasExpiringSoon = item.batches.some(
                  (b) => !b.isExpired && b.daysUntilExpiration !== null && b.daysUntilExpiration <= 30,
                );

                return (
                  <tr key={item.labKitId} className="group hover:bg-slate-800/40 transition-colors">
                    <td colSpan={7} className="p-0">
                      {/* Main Kit Row */}
                      <div
                        onClick={() => toggleExpand(item.labKitId)}
                        className="flex items-center w-full px-4 py-3.5 cursor-pointer select-none"
                      >
                        <div className="w-10 shrink-0">
                          {item.expiration && item.batches.length > 0 ? (
                            <button
                              type="button"
                              aria-label={isExpanded ? 'Zwiń partie' : 'Rozwiń partie'}
                              className="text-slate-400 hover:text-cyan-400"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                        </div>

                        <div className="flex-1 font-medium text-slate-100 flex items-center gap-2">
                          <span>{item.kitName}</span>
                          {hasExpiredBatches && (
                            <span
                              title="Zawiera przeterminowane partie w magazynie (REQ-KIT-026)"
                              className="inline-flex items-center gap-1 rounded-sm bg-rose-950/80 border border-rose-800/60 px-2 py-0.5 text-[11px] font-semibold text-rose-300"
                            >
                              <AlertCircle className="h-3 w-3" />
                              <span>Przeterminowany</span>
                            </span>
                          )}
                          {!hasExpiredBatches && hasExpiringSoon && (
                            <span
                              title="Partia wygasa w ciągu 30 dni"
                              className="inline-flex items-center gap-1 rounded-sm bg-amber-950/80 border border-amber-800/60 px-2 py-0.5 text-[11px] font-semibold text-amber-300"
                            >
                              <Clock className="h-3 w-3" />
                              <span>Krótka data</span>
                            </span>
                          )}
                        </div>

                        <div className="w-48 text-xs text-slate-300 truncate">
                          {item.laboratoryName}
                        </div>

                        <div className="w-44 text-xs text-slate-400 flex items-center gap-1.5">
                          {item.localisation ? (
                            <>
                              <MapPin className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                              <span className="truncate">{item.localisation}</span>
                            </>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </div>

                        <div className="w-36 text-xs">
                          {item.expiration ? (
                            <span className="inline-flex items-center gap-1 rounded-sm bg-cyan-950/50 border border-cyan-800/40 px-2 py-0.5 text-cyan-300 font-mono text-[11px]">
                              <Calendar className="h-3 w-3" />
                              <span>Terminowy</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-sm bg-slate-800 border border-slate-700 px-2 py-0.5 text-slate-400 text-[11px]">
                              <span>Bezterminowy</span>
                            </span>
                          )}
                        </div>

                        <div className="w-32 text-right">
                          <span
                            title={
                              item.totalStock < 0
                                ? 'Ujemny stan magazynowy — bilans zużyć i korekt przekroczył przyjęcia (D-C)'
                                : undefined
                            }
                            className={`inline-block font-mono text-sm font-semibold px-2 py-0.5 rounded-sm ${
                              item.totalStock > 0
                                ? 'text-emerald-400 bg-emerald-950/30'
                                : item.totalStock < 0
                                  ? 'text-rose-300 bg-rose-950/50 border border-rose-800/60'
                                  : 'text-slate-500 bg-slate-800/30'
                            }`}
                          >
                            {item.totalStock} szt.
                          </span>
                        </div>

                        <div className="w-28 text-right text-xs text-slate-400 font-mono">
                          {item.expiration ? `${item.batches.length} partii` : '1 zasób'}
                        </div>
                      </div>

                      {/* Accordion: FEFO Batches List */}
                      {isExpanded && item.expiration && (
                        <div className="bg-slate-950/70 border-t border-slate-800 px-10 py-3">
                          <div className="rounded-lg border border-slate-800/80 bg-slate-900/90 overflow-hidden">
                            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2 bg-slate-950/50 text-xs font-medium text-slate-400">
                              <span className="flex items-center gap-1.5 text-cyan-400">
                                <Layers className="h-3.5 w-3.5" />
                                <span>Kolejka wydań FEFO (najkrótsza data wydawana w pierwszej kolejności)</span>
                              </span>
                              <span className="text-[11px] text-slate-500">
                                REQ-KIT-019, REQ-KIT-026
                              </span>
                            </div>

                            {item.batches.length === 0 ? (
                              <div className="p-4 text-center text-xs text-slate-500">
                                Brak zarejestrowanych partii z fizycznym stanem
                              </div>
                            ) : (
                              <table className="w-full text-xs text-left">
                                <thead className="border-b border-slate-800 bg-slate-950/40 text-[11px] text-slate-400 font-semibold uppercase">
                                  <tr>
                                    <th className="px-4 py-2">Seria / Partia</th>
                                    <th className="px-4 py-2">Data ważności</th>
                                    <th className="px-4 py-2">Status przydatności</th>
                                    <th className="px-4 py-2 text-right">Stan partii</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/60 font-mono">
                                  {item.batches.map((batch, bIndex) => {
                                    return (
                                      <tr
                                        key={bIndex}
                                        className={
                                          batch.isExpired
                                            ? 'bg-rose-950/20 text-rose-200'
                                            : bIndex === 0
                                              ? 'bg-cyan-950/20 text-slate-200 font-semibold'
                                              : 'text-slate-300'
                                        }
                                      >
                                        <td className="px-4 py-2.5">
                                          <div className="flex items-center gap-2">
                                            {bIndex === 0 && (
                                              <span
                                                title="Sugerowane pierwsze wydanie FEFO"
                                                className="h-2 w-2 rounded-full bg-cyan-400"
                                              />
                                            )}
                                            <span>{batch.batchNumber ?? 'Brak numeru partii'}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                          {batch.expirationDate ?? '—'}
                                        </td>
                                        <td className="px-4 py-2.5 font-sans">
                                          {batch.isExpired ? (
                                            <span className="inline-flex items-center gap-1.5 text-rose-400 font-semibold">
                                              <AlertCircle className="h-3.5 w-3.5" />
                                              <span>
                                                Przeterminowany ({Math.abs(batch.daysUntilExpiration ?? 0)} dni temu)
                                              </span>
                                            </span>
                                          ) : batch.daysUntilExpiration !== null &&
                                            batch.daysUntilExpiration <= 30 ? (
                                            <span className="inline-flex items-center gap-1.5 text-amber-400 font-medium">
                                              <AlertTriangle className="h-3.5 w-3.5" />
                                              <span>Wygasa za {batch.daysUntilExpiration} dni</span>
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1.5 text-emerald-400">
                                              <CheckCircle2 className="h-3.5 w-3.5" />
                                              <span>
                                                Ważny ({batch.daysUntilExpiration} dni do końca)
                                              </span>
                                            </span>
                                          )}
                                        </td>
                                        <td
                                          className={`px-4 py-2.5 text-right font-semibold ${
                                            batch.quantity < 0 ? 'text-rose-400' : ''
                                          }`}
                                          title={
                                            batch.quantity < 0
                                              ? 'Ujemny stan partii (D-C)'
                                              : undefined
                                          }
                                        >
                                          {batch.quantity} szt.
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
